/**
 * Экран горничной «Уборка» (процесс выселения, housekeeping wave 2026-06-18).
 *
 * Очередь = номера «На проверке» (pending_inspection) и «На уборке» (cleaning).
 * Цикл выезда по карточке:
 *   «На проверке» → кнопка «Номер проверен»  → «На уборке»  (или «В ремонт»)
 *   «На уборке»   → кнопка «Закрыть заявку»   → «Свободен»
 *
 * RBAC: PATCH /v1/rooms/{id}/status гейтится на бэке (mgmt + housekeeper, #331);
 * экран доступен горничной/менеджеру/владельцу. Гость модуль не видит.
 */
import { useMemo } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { RoomView } from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';
import {
  useCleaningQueue,
  useCloseCleaning,
  useMarkInspected,
  useRoomTypeNames,
  useSendToRepair,
} from './useHousekeeping.js';

export interface HousekeepingPageProps {
  /** Передаётся Workspace; экран горничной не зависит от управляющей роли. */
  canManage?: boolean;
}

export function HousekeepingPage(_props: HousekeepingPageProps): JSX.Element {
  const slug = useMemo(() => resolveTenantSlug(), []);
  const { items, isLoading, isError, hasData } = useCleaningQueue();
  const typesQ = useRoomTypeNames(slug);

  const typeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of typesQ.data?.room_types ?? []) map.set(t.id, t.name);
    return map;
  }, [typesQ.data]);

  return (
    <section data-testid="housekeeping-page">
      <h1 className="hsc-page__title">Уборка</h1>
      <p className="ta-muted" data-testid="housekeeping-subtitle">
        Номера после выезда гостей. Осмотрите номер, проведите уборку и закройте
        заявку — номер станет «Свободен».
      </p>

      {isLoading ? (
        <div className="fleet-grid" data-testid="housekeeping-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={140} radius={16} />
          ))}
        </div>
      ) : isError || !hasData ? (
        <Card>
          <p role="alert">Не удалось загрузить список номеров. Обновите страницу.</p>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <p data-testid="housekeeping-empty">Сейчас номеров на уборку нет. Всё чисто 🧹</p>
        </Card>
      ) : (
        <ul className="fleet-grid" data-testid="housekeeping-queue">
          {items.map((room) => (
            <HousekeepingCard
              key={room.id}
              room={room}
              typeName={typeName.get(room.type_id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function HousekeepingCard({
  room,
  typeName,
}: {
  room: RoomView;
  typeName?: string | undefined;
}): JSX.Element {
  const inspected = useMarkInspected();
  const close = useCloseCleaning();
  const repair = useSendToRepair();

  const isPending = room.status === 'pending_inspection';
  const statusLabel = isPending ? 'На проверке' : 'На уборке';
  const busy = inspected.isPending || close.isPending || repair.isPending;
  const errored = inspected.isError || close.isError || repair.isError;

  return (
    <li className="fleet-card" data-testid={`hk-room-${room.number}`}>
      <div className="fleet-card__head">
        <span className="fleet-card__title">№ {room.number}</span>
        <span
          className={`fleet-badge ${isPending ? 'fleet-badge--info' : 'fleet-badge--warn'}`}
          data-testid={`hk-room-${room.number}-status`}
        >
          {statusLabel}
        </span>
      </div>
      <div className="fleet-card__meta">
        <span>{room.floor} этаж</span>
        {typeName !== undefined && <span>· {typeName}</span>}
      </div>
      {room.notes !== undefined && room.notes !== '' && (
        <p className="fleet-card__notes">{room.notes}</p>
      )}

      <div className="fleet-card__actions">
        {isPending ? (
          <>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => inspected.mutate({ id: room.id })}
              data-testid={`hk-room-${room.number}-inspect`}
            >
              Номер проверен
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => repair.mutate({ id: room.id })}
              data-testid={`hk-room-${room.number}-repair`}
            >
              В ремонт
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => close.mutate({ id: room.id })}
            data-testid={`hk-room-${room.number}-close`}
          >
            Закрыть заявку
          </Button>
        )}
        {errored && (
          <p role="alert" className="fleet-card__err">
            Не удалось выполнить действие. Возможно, недостаточно прав.
          </p>
        )}
      </div>
    </li>
  );
}
